import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdersStore, OrderStatus } from '@/store/orders';
import { useRiderStore } from '@/store/rider';
import { useAuthStore } from '@/store/auth';
import { ordersApi } from '@/lib/api';
import { logger } from '@/lib/logger';
import StatusToggle from '@/components/rider/StatusToggle';
import OrderCard from '@/components/orders/OrderCard';
import OrderDetailsModal from '@/components/orders/OrderDetailsModal';
import ActiveOrderCard from '@/components/orders/ActiveOrderCard';
// import WSStatusIndicator from '@/components/WSStatusIndicator'; // Replaced with rider status
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useOrderUpdates } from '@/hooks/useOrderUpdates';
import { useGPSTracking } from '@/hooks/useGPSTracking';
import { LogOut } from 'lucide-react';

export default function OrdersPage() {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'available' | 'active'>('available');

  // Network status
  useOnlineStatus();

  // WebSocket connection
  useWebSocket();

  // Real-time subscriptions
  useOrderUpdates();
  useGPSTracking();

  // Stores
  const availableOrders = useOrdersStore((state) => state.availableOrders);
  const activeOrder = useOrdersStore((state) => state.activeOrder);
  const isLoadingAvailable = useOrdersStore((state) => state.isLoadingAvailable);
  const isAccepting = useOrdersStore((state) => state.isAccepting);
  const isUpdatingStatus = useOrdersStore((state) => state.isUpdatingStatus);
  
  const setAvailableOrders = useOrdersStore((state) => state.setAvailableOrders);
  const acceptOrder = useOrdersStore((state) => state.acceptOrder);
  const removeAvailableOrder = useOrdersStore((state) => state.removeAvailableOrder);
  const updateOrderStatus = useOrdersStore((state) => state.updateOrderStatus);
  const setIsLoadingAvailable = useOrdersStore((state) => state.setIsLoadingAvailable);
  const setIsAccepting = useOrdersStore((state) => state.setIsAccepting);
  const setIsUpdatingStatus = useOrdersStore((state) => state.setIsUpdatingStatus);
  const setBusy = useRiderStore((state) => state.setBusy);
  const setAvailable = useRiderStore((state) => state.setAvailable);

  const riderStatus = useRiderStore((state) => state.status);
  const initializeRider = useRiderStore((state) => state.initializeRider);
  const { logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Initialize rider profile on mount
  useEffect(() => {
    initializeRider();
  }, []);

  // Load available orders
  useEffect(() => {
    if (riderStatus === 'available' && !activeOrder) {
      loadAvailableOrders();
    }
  }, [riderStatus, activeOrder]);

  // Switch to active tab when order is accepted
  useEffect(() => {
    if (activeOrder) {
      setActiveTab('active');
      setBusy();
    } else if (riderStatus === 'busy') {
      setAvailable();
    }
  }, [activeOrder]);

  const loadAvailableOrders = async () => {
    try {
      setIsLoadingAvailable(true);
      const rawOrders = await ordersApi.getAvailable();

      // Transform flat API response to nested Order structure
      // NOTE: API returns amounts as-is (pesos for old orders, may be inconsistent)
      const orders = (rawOrders || []).map((rawOrder: any) => ({
        id: rawOrder.id,
        customer: {
          id: rawOrder.customer_id || rawOrder.customer?.id || '',
          name: rawOrder.customer?.name || rawOrder.customer_name || 'Customer',
          phone: rawOrder.customer?.phone || rawOrder.customer_phone || '',
        },
        vendor: {
          id: rawOrder.vendor_id,
          name: rawOrder.vendor?.name || rawOrder.vendor_name || 'Unknown Vendor',
          address: rawOrder.vendor?.address || rawOrder.vendor_address || 'Pickup Location',
          location: {
            lat: rawOrder.vendor?.lat || rawOrder.vendor_lat || 13.155,
            lng: rawOrder.vendor?.lng || rawOrder.vendor_lng || 123.74,
          },
        },
        delivery: {
          address: rawOrder.delivery_address || '',
          location: {
            lat: rawOrder.delivery_lat || 0,
            lng: rawOrder.delivery_lng || 0,
          },
          instructions: rawOrder.delivery_notes || '',
        },
        items: [], // Items not included in available orders list (need to fetch separately)
        totalAmount: rawOrder.total || 0, // Use as-is from API (no conversion)
        deliveryFee: rawOrder.delivery_fee || 0, // Use as-is from API (no conversion)
        status: rawOrder.status,
        paymentMethod: rawOrder.payment_method || 'cod',
        distance: 0, // TODO: Calculate from vendor to delivery location
        createdAt: rawOrder.created_at,
        acceptedAt: rawOrder.accepted_at,
        pickedUpAt: rawOrder.picked_up_at,
        deliveredAt: rawOrder.delivered_at,
      }));

      setAvailableOrders(orders);
      logger.info('Available orders loaded', { count: orders.length });
    } catch (error) {
      logger.error('Failed to load available orders', error as Error);
    } finally {
      setIsLoadingAvailable(false);
    }
  };

  const handleAcceptOrder = async (orderId: string) => {
    try {
      setIsAccepting(true);
      const response = await ordersApi.accept(orderId);
      
      // Use fresh data from API response (claim-order returns enriched data)
      if (response.success && response.data?.order) {
        const apiOrder = response.data.order;
        
        // Map API response to Order structure with fresh data
        const freshOrder = {
          id: apiOrder.id,
          customer: {
            id: apiOrder.customer_id,
            name: apiOrder.customer?.name || 'Customer',
            phone: apiOrder.customer?.phone || '',
          },
          vendor: {
            id: apiOrder.vendor_id,
            name: apiOrder.vendor?.name || 'Unknown Vendor',
            address: apiOrder.vendor?.address || 'Pickup Location',
            location: {
              lat: apiOrder.vendor?.location?.lat || 13.155,
              lng: apiOrder.vendor?.location?.lng || 123.74,
            },
          },
          delivery: {
            address: apiOrder.delivery?.address || apiOrder.delivery_address || '',
            location: {
              lat: apiOrder.delivery?.location?.lat || parseFloat(apiOrder.delivery_lat) || 0,
              lng: apiOrder.delivery?.location?.lng || parseFloat(apiOrder.delivery_lng) || 0,
            },
            instructions: apiOrder.delivery_notes || '',
          },
          items: [],
          totalAmount: parseFloat(apiOrder.total) || 0,
          deliveryFee: parseFloat(apiOrder.delivery_fee) || 0,
          status: apiOrder.status,
          paymentMethod: apiOrder.payment_method || 'cod',
          distance: 0,
          createdAt: apiOrder.created_at,
          acceptedAt: apiOrder.accepted_at || new Date().toISOString(),
          pickedUpAt: apiOrder.picked_up_at,
          deliveredAt: apiOrder.delivered_at,
        };
        
        // Set fresh order as active order directly
        useOrdersStore.getState().setActiveOrder(freshOrder);
        removeAvailableOrder(orderId);
        logger.info('Order claimed with fresh data', { orderId, vendorName: freshOrder.vendor.name, customerName: freshOrder.customer.name });
      } else {
        // Fallback to old behavior if API doesn't return data
        acceptOrder(orderId);
        logger.warn('Claim response missing order data, using store data', { orderId });
      }
      
      logger.info('Order claimed successfully', { orderId });
    } catch (error) {
      logger.error('Failed to claim order', error as Error);
      throw error;
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDeclineOrder = async (orderId: string) => {
    try {
      // Sprint 27.3: Notify backend of rejection for smart assignment cascade
      await ordersApi.reject(orderId);
      logger.info('Order assignment rejected', { orderId });
    } catch (error) {
      // Still remove locally even if API call fails
      logger.warn('Failed to notify backend of rejection', { orderId, error });
    }
    removeAvailableOrder(orderId);
    setSelectedOrder(null);
  };

  const handleUpdateOrderStatus = async (status: OrderStatus) => {
    if (!activeOrder) return;

    try {
      setIsUpdatingStatus(true);
      await ordersApi.updateStatus(activeOrder.id, status);
      updateOrderStatus(activeOrder.id, status);
      logger.info('Order status updated', { orderId: activeOrder.id, status });
    } catch (error) {
      logger.error('Failed to update order status', error as Error);
      throw error;
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const selectedOrderData = availableOrders.find(o => o.id === selectedOrder);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-10">
        <div className="p-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Orders</h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full ${
                riderStatus === 'available' ? 'bg-green-500 animate-pulse' : 
                riderStatus === 'busy' ? 'bg-yellow-500 animate-pulse' : 
                'bg-gray-400'
              }`} />
              <span className={`font-medium ${
                riderStatus === 'available' ? 'text-green-600' : 
                riderStatus === 'busy' ? 'text-yellow-600' : 
                'text-gray-600'
              }`}>
                {riderStatus === 'available' ? 'Online' : 
                 riderStatus === 'busy' ? 'Busy' : 
                 'Offline'}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-t">
          <button
            onClick={() => setActiveTab('available')}
            className={`flex-1 py-3 font-medium transition-colors ${
              activeTab === 'available'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground'
            }`}
          >
            Available {availableOrders.length > 0 && `(${availableOrders.length})`}
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-3 font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground'
            }`}
          >
            Active {activeOrder && '(1)'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Status Toggle (always visible) */}
        <StatusToggle />

        {/* Available Orders Tab */}
        {activeTab === 'available' && (
          <div className="space-y-4">
            {riderStatus === 'offline' && (
              <div className="bg-muted border rounded-lg p-6 text-center">
                <div className="text-muted-foreground mb-2">
                  You are currently offline
                </div>
                <div className="text-sm text-muted-foreground">
                  Go online to start receiving orders
                </div>
              </div>
            )}

            {riderStatus !== 'offline' && isLoadingAvailable && (
              <div className="text-center py-8">
                <svg className="animate-spin h-8 w-8 mx-auto text-primary" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <p className="text-muted-foreground mt-2">Loading orders...</p>
              </div>
            )}

            {riderStatus !== 'offline' && !isLoadingAvailable && availableOrders.length === 0 && (
              <div className="bg-muted border rounded-lg p-6 text-center">
                <div className="text-4xl mb-2">📦</div>
                <div className="font-medium mb-1">No orders available</div>
                <div className="text-sm text-muted-foreground">
                  New orders will appear here automatically
                </div>
              </div>
            )}

            {availableOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onPress={() => setSelectedOrder(order.id)}
              />
            ))}
          </div>
        )}

        {/* Active Order Tab */}
        {activeTab === 'active' && (
          <div>
            {activeOrder ? (
              <ActiveOrderCard
                order={activeOrder}
                onUpdateStatus={handleUpdateOrderStatus}
                isUpdating={isUpdatingStatus}
              />
            ) : (
              <div className="bg-muted border rounded-lg p-6 text-center">
                <div className="text-4xl mb-2">✅</div>
                <div className="font-medium mb-1">No active order</div>
                <div className="text-sm text-muted-foreground">
                  Accept an order to start delivery
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrderData && (
        <OrderDetailsModal
          order={selectedOrderData}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onAccept={handleAcceptOrder}
          onDecline={handleDeclineOrder}
          isAccepting={isAccepting}
        />
      )}
    </div>
  );
}
